import React from "react";
import { Whisper, Popover } from "rsuite";
import styled from "styled-components";
import CloseIcon from "@rsuite/icons/Close";
import SegmentedPicker from "../SegmentedPicker/SegmentedPicker";
import { colors } from "../theme/colors";
import { shadows } from "../theme/shadows";

const MultiSectionFilterPopover = ({
  sections = [],
  trigger,
  placement = "bottomStart",
  ...props
}) => {
  const whisperRef = React.useRef();
  const visibleSections = sections.filter((section) => section.visible);

  const handleClose = () => {
    if (whisperRef.current) {
      whisperRef.current.close();
    }
  };

  const popoverContent = (
    <StyledPopover>
      <PopoverHeader>
        <PopoverTitle>Status Filters</PopoverTitle>
        <CloseButton onClick={handleClose} aria-label="Close filters">
          <CloseIcon style={{ fontSize: "16px" }} />
        </CloseButton>
      </PopoverHeader>
      {visibleSections.map((section, index) => (
        <FilterSection
          key={section.id}
          isLast={index === visibleSections.length - 1}
        >
          <SectionLabel>{section.label}</SectionLabel>
          <SegmentedPicker
            options={section.options}
            activeKey={section.value}
            onSelect={section.onChange}
            size="small"
            variant="subtle"
          />
        </FilterSection>
      ))}
    </StyledPopover>
  );

  return (
    <Whisper
      ref={whisperRef}
      placement={placement}
      trigger="click"
      speaker={popoverContent}
      enterable
      {...props}
    >
      <span style={{ display: "inline-block" }}>{trigger}</span>
    </Whisper>
  );
};

const StyledPopover = styled(Popover)`
  width: 320px;
  border-radius: 16px;
  box-shadow: ${shadows.lg};
  backdrop-filter: blur(12px);
  background: rgba(255, 255, 255, 0.98);
  border: 1px solid rgba(0, 0, 0, 0.08);
  padding: 0;

  .rs-popover-arrow {
    display: none;
  }
`;

const PopoverHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);
  background: rgba(0, 0, 0, 0.02);
`;

const PopoverTitle = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: ${colors.neutral[800]};
`;

const CloseButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  border: none;
  background: #e74c3c;
  color: white;
  cursor: pointer;
  transition: all 0.2s ease;
  padding: 0;

  &:hover {
    background: #c0392b;
    transform: scale(1.05);
  }

  &:active {
    transform: scale(0.95);
  }

  &:focus {
    outline: none;
    box-shadow: 0 0 0 3px rgba(231, 76, 60, 0.2);
  }
`;

const FilterSection = styled.div`
  padding: 16px 20px;
  border-bottom: ${({ isLast }) =>
    isLast ? "none" : "1px solid rgba(0, 0, 0, 0.06)"};

  &:last-child {
    border-bottom: none;
  }
`;

const SectionLabel = styled.label`
  display: block;
  font-size: 12px;
  font-weight: 600;
  color: ${colors.neutral[600]};
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 12px;
`;

export default MultiSectionFilterPopover;
